import React, { useCallback, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import EventCard from '../UI/EventCard'
import VenueList from '../UI/VenueList'
import EventConfirmExitDialog from '../UI/EventConfirmExitDialog'
import anearApi from '../Api/AnearApi'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import {
  canHostClosePlayableEvent,
  hostCloseConfirmationCopy,
  hostCloseTransitionState
} from '../Utils/eventHostActions'

import ROUTES from '../../routesConfig'

const normalizeVenueId = (value) => String(value || '').trim()

const EventList = ({
  playableEvents,
  playableVenues = [],
  message = "No Events Found",
  onEventsChanged
}) => {
  const location = useLocation()
  const fromPath = `${location.pathname}${location.search || ''}`
  const [transitioningEventId, setTransitioningEventId] = useState(null)
  const [pendingClose, setPendingClose] = useState(null)
  const [transitionError, setTransitionError] = useState(null)

  const isHostingPlayableEvent = (playableEvent) => {
    const { playableState, anearEvent } = playableEvent
    const isCreated = playableState === 'creator' || anearEvent.isCreator?.()
    const isInviter = playableState === 'inviter'
    return isCreated || isInviter
  }

  // First, derive logical buckets so that each event appears in only one section.
  // Hosted/created events should always show under "Events you're hosting".
  const hostingEvents = playableEvents.filter(isHostingPlayableEvent)

  const inEvents = playableEvents.filter(pe => {
    // Do not duplicate anything that's already considered "hosting"
    const isHosting =
      hostingEvents.includes(pe) ||
      isHostingPlayableEvent(pe)

    const { playableState } = pe
    const isInStates = ['participant', 'spectator'].includes(playableState)
    return !isHosting && isInStates
  })

  const getVenueIdForPlayableEvent = (playableEvent) => normalizeVenueId(
    playableEvent?.anearEvent?.zone?.venue?.id
  )

  const isVenueBackedPlayableEvent = (playableEvent) => Boolean(getVenueIdForPlayableEvent(playableEvent))

  const venueById = new Map(
    (playableVenues || [])
      .map((playableVenue) => [normalizeVenueId(playableVenue?.venue?.id || playableVenue?.id), playableVenue])
      .filter(([venueId]) => venueId)
  )

  const isHostingPlayableVenue = (playableVenue) => {
    const venue = playableVenue?.venue
    if (!venue) return false

    const hasHostAccess = venue.venueZoneAssignments?.().some((assignment) => assignment?.host_access)
    const hasHostedEvent = venue.events?.().some((event) => event?.isCreator?.())
    return hasHostAccess || hasHostedEvent
  }

  const buildVenueEntriesFromEvents = (events, excludedVenueIds = new Set()) => {
    const seenVenueIds = new Set(excludedVenueIds)

    return events.reduce((entries, playableEvent) => {
      const venueId = getVenueIdForPlayableEvent(playableEvent)
      if (!venueId || seenVenueIds.has(venueId)) return entries

      const fallbackVenue = playableEvent?.anearEvent?.zone?.venue
      const playableVenue = venueById.get(venueId)
      const venue = playableVenue?.venue || fallbackVenue
      if (!venue) return entries

      seenVenueIds.add(venueId)
      entries.push({
        id: venueId,
        venue
      })
      return entries
    }, [])
  }

  const invitationEvents = playableEvents.filter(
    pe => pe.playableState === 'invitee'
  )

  const nearbyEvents = playableEvents.filter(pe =>
    ['open', 'open_house', 'public'].includes(pe.playableState)
  )

  const hostingPlayableVenues = playableVenues.filter(isHostingPlayableVenue)
  const hostingPlayableVenueIds = new Set(
    hostingPlayableVenues.map((playableVenue) => normalizeVenueId(playableVenue?.venue?.id || playableVenue?.id)).filter(Boolean)
  )
  const joinedPlayableVenues = playableVenues.filter((playableVenue) => {
    const venueId = normalizeVenueId(playableVenue?.venue?.id || playableVenue?.id)
    return venueId && !hostingPlayableVenueIds.has(venueId)
  })

  const shownVenueIds = new Set([
    ...hostingPlayableVenues.map((playableVenue) => normalizeVenueId(playableVenue?.venue?.id || playableVenue?.id)),
    ...joinedPlayableVenues.map((playableVenue) => normalizeVenueId(playableVenue?.venue?.id || playableVenue?.id))
  ].filter(Boolean))

  const invitationVenueEntries = buildVenueEntriesFromEvents(invitationEvents, shownVenueIds)
  invitationVenueEntries.forEach((entry) => shownVenueIds.add(normalizeVenueId(entry?.id)))
  const nearbyVenueEntries = buildVenueEntriesFromEvents(nearbyEvents, shownVenueIds)

  const venueSections = [
    { title: 'Venues You\'re Hosting', venues: hostingPlayableVenues },
    { title: 'Venues You Joined', venues: joinedPlayableVenues },
    { title: 'Venue Invitations', venues: invitationVenueEntries },
    { title: 'Nearby Venues', venues: nearbyVenueEntries }
  ].filter((group) => group.venues.length > 0)

  const sections = [
    { title: 'Events You\'re Hosting', events: hostingEvents.filter((event) => !isVenueBackedPlayableEvent(event)) },
    { title: 'Events You\'re In', events: inEvents.filter((event) => !isVenueBackedPlayableEvent(event)) },
    { title: 'Event Invitations', events: invitationEvents.filter((event) => !isVenueBackedPlayableEvent(event)) },
    { title: 'Nearby Events', events: nearbyEvents.filter((event) => !isVenueBackedPlayableEvent(event)) }
  ].filter(group => group.events.length > 0)

  // Reorder sections: Nearby Events first (if present), then Events You're In
  // This provides better UX by showing relevant nearby events before user's existing events
  const orderedSections = [...sections].sort((a, b) => {
    // Nearby Events comes first (if present)
    if (a.title === 'Nearby Events') return -1
    if (b.title === 'Nearby Events') return 1
    // Events You're In comes second (after Nearby Events, if present)
    if (a.title === 'Events You\'re In') {
      return sections.some(s => s.title === 'Nearby Events') ? 1 : -1
    }
    if (b.title === 'Events You\'re In') {
      return sections.some(s => s.title === 'Nearby Events') ? -1 : 1
    }
    // Keep other sections in original order
    return 0
  })

  const requestCloseConfirmation = useCallback((playableEvent) => {
    const anearEvent = playableEvent.anearEvent
    const eventTitle = anearEvent?.name || 'Event'
    const nextState = hostCloseTransitionState(anearEvent)
    const appAttributes = anearEvent?.zone?.app?.attributes || {}
    const appIconUrl = appAttributes['icon-url'] || null
    const appShortName = appAttributes['short-name'] || 'App'

    setTransitionError(null)
    setPendingClose({
      eventId: String(anearEvent.id),
      nextState,
      appIconUrl,
      appShortName,
      ...hostCloseConfirmationCopy(nextState, eventTitle, anearEvent?.state)
    })
  }, [])

  const cancelCloseConfirmation = useCallback(() => {
    setPendingClose(null)
  }, [])

  const confirmClose = useCallback(async () => {
    if (!pendingClose) return

    const { eventId, nextState } = pendingClose
    setPendingClose(null)
    setTransitionError(null)
    setTransitioningEventId(eventId)

    try {
      await anearApi.transitionPlayableEvent(eventId, nextState)
      if (typeof onEventsChanged === 'function') {
        await onEventsChanged()
      }
    } catch (err) {
      console.error(err)
      setTransitionError(err)
    } finally {
      setTransitioningEventId(null)
    }
  }, [onEventsChanged, pendingClose])

  const renderEventCard = (playableEvent) => {
    const anearEvent = playableEvent.anearEvent
    const showCloseControl = canHostClosePlayableEvent(playableEvent, isHostingPlayableEvent)
    const isClosing = transitioningEventId === String(anearEvent.id)

    return (
      <Box key={anearEvent.id} sx={{ width: '100%', minWidth: 0 }}>
        <EventCard
          playableEvent={playableEvent}
          joinTo={ROUTES.EVENT_PAGE(anearEvent.slug)}
          joinState={{ from: fromPath }}
          showCloseControl={showCloseControl}
          closeDisabled={isClosing}
          onRequestClose={() => requestCloseConfirmation(playableEvent)}
        />
      </Box>
    )
  }

  return (
    <Box sx={{
      bgcolor: '#0B0F19',
      minHeight: 'calc(100svh - 80px)',
      height: 'calc(100svh - 80px)',
      borderRadius: 2,
      p: 1.5,
      overflowX: 'hidden',
      overflowY: 'auto',
      width: '100%',
      boxSizing: 'border-box',
      maxWidth: '100%'
    }}>
      {transitionError ? (
        <Typography sx={{ color: '#FCA5A5', mb: 1.5, textAlign: 'center' }}>
          {transitionError.message || 'Unable to update this event.'}
        </Typography>
      ) : null}
      {venueSections.map((group) => (
        <VenueList
          key={group.title}
          playableVenues={group.venues}
          title={group.title}
        />
      ))}
      {venueSections.length > 0 || orderedSections.length > 0 ? (
        orderedSections.map((group) => (
          <Box key={group.title}>
            <Typography
              variant="h6"
              sx={{
                padding: '0.5rem 0',
                fontWeight: 500,
                color: 'rgba(230,233,239,0.8)',
                fontSize: '0.9rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}
            >
              {group.title}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
              {group.events.map(renderEventCard)}
            </Box>
          </Box>
        ))
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8 }}>
          {transitionError ? (
            <Typography sx={{ color: '#FCA5A5', mb: 2, textAlign: 'center' }}>
              {transitionError.message || 'Unable to update this event.'}
            </Typography>
          ) : null}
          <Typography variant="h6" sx={{ color: 'rgba(230,233,239,0.8)' }}>
            {message}
          </Typography>
          <Box sx={{ mt: 4 }}>
            <Link
              to={ROUTES.APPS}
              style={{ textDecoration: 'none' }}
            >
              <Typography sx={{ color: '#3B82F6', '&:hover': { textDecoration: 'underline' } }}>
                Create a New Event
              </Typography>
            </Link>
          </Box>
        </Box>
      )}
      <EventConfirmExitDialog
        open={Boolean(pendingClose)}
        anearEvent={pendingClose
          ? {
              zone: {
                app: {
                  iconUrl: pendingClose.appIconUrl || null,
                  shortName: pendingClose.appShortName || 'App'
                }
              }
            }
          : null}
        title={pendingClose?.confirmTitle}
        body={pendingClose?.confirmBody}
        stayLabel={pendingClose?.stayLabel}
        exitLabel={pendingClose?.exitLabel}
        onStay={cancelCloseConfirmation}
        onExit={confirmClose}
      />
    </Box>
  )
}

export default EventList
